import { Spacing, Shadows, Media, BorderRadius, withTheming } from '@doordash/design-language';
import { CloseButtonSides, Sizes, BreakpointModalWidths } from './constants.js';
import { renderOnLayer, Layers, AnimationTypes } from '@doordash/component-layer-manager';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import React from 'react';
import { get } from 'lodash';
import { getMobileDevice } from './getEnvironmentConstants.js';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13;

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _taggedTemplateLiteral(strings, raw) { if (!raw) { raw = strings.slice(0); } return Object.freeze(Object.defineProperties(strings, { raw: { value: Object.freeze(raw) } })); }
var DEFAULT_MODAL_PADDING = Spacing.Small;
var OS = getMobileDevice();

var getMobileHeight = function getMobileHeight() {
  // If we're on Android, we need to account for
  // the URL Bar Resizing behavior that's described
  // here: https://developers.google.com/web/updates/2016/12/url-bar-resizing
  // We're relying on the fact that position: fixed
  // elements have an exception in that it will
  // always fill the visible height, even if the URL bar
  // is showing or hiding.
  if (OS === 'Android') {
    return '100%';
  } // This is essentially the equivilant of 100vh on
  // iOS/iPadOS as it accounts for the address bar,
  // toolbar, and smartbanners.


  if (OS === 'iPadOS' || OS === 'iOS') {
    return '-webkit-fill-available';
  } // By default, just take up the entire viewport height.


  return '100vh';
};

var MOBILE_HEIGHT = getMobileHeight();

var getMaxWidthForSize = function getMaxWidthForSize(props) {
  if (props.size === Sizes.Custom && get(props.customSize, 'maxWidth')) {
    return "".concat(props.customSize.maxWidth, "px");
  }

  if (props.size === Sizes.FullScreen) {
    return "100vw";
  }

  if (props.modalWidth) {
    return "".concat(props.modalWidth, "px");
  }

  return "".concat(BreakpointModalWidths.Desktop[Sizes.Large], "px");
};

var getHeightForSize = function getHeightForSize(props) {
  switch (props.size) {
    case Sizes.FullScreen:
      return '100vh';

    default:
      return '0';
  }
};

var getMaxHeightForSize = function getMaxHeightForSize(props) {
  if (props.isFooterFixedPosition && (OS === 'iOS' || OS === 'iPadOS')) {
    // To properly handle any changes to the "available" real estate
    // on the screen with iOS Safari address & navigation bar expand
    // and collapse behavior and keep the overlay spacing around tablet
    // Modals. This logic "shrinks" the ModalContainer to be just behind
    // the top line of the fixed footer container, so that the Modal appears
    // to be a seamless unit while still preserving the padding on all sides
    return "calc(".concat(props.viewportHeight, "px - ").concat(props.modalPadding * 4, "px)");
  }

  var defaultMaxHeight = props.viewportHeight - props.modalPadding * 2;

  if (props.size === Sizes.Custom && get(props.customSize, 'maxHeight') && props.customSize.maxHeight <= defaultMaxHeight) {
    return "".concat(props.customSize.maxHeight, "px");
  }

  return "calc(100vh - ".concat(props.modalPadding * 2, "px)");
};

var getBorderRadiusForSize = function getBorderRadiusForSize(props) {
  switch (props.size) {
    case Sizes.FullScreen:
      return '0px';

    default:
      return "".concat(props.modalBorderRadius, "px");
  }
};

var ModalContainer = styled.div.withConfig({
  displayName: "styles__ModalContainer",
  componentId: "sc-1r4qbfh-0"
})(["position:relative;background:", ";box-shadow:", ";min-height:", ";max-height:", ";max-width:", ";width:calc(100vw - ", "px);border-radius:", ";display:flex;flex-direction:column;overflow:hidden;-ms-overflow-y:scroll;outline:none;bottom:", "px;", ";"], function (props) {
  return props.modalBackgroundColor;
}, Shadows.Large, getHeightForSize, getMaxHeightForSize, getMaxWidthForSize, function (props) {
  return props.modalPadding * 2;
}, getBorderRadiusForSize, function (props) {
  return props.isFooterFixedPosition && (OS === 'iOS' || OS === 'iPadOS') ? props.bottomLocation > 0 ? props.bottomLocation : props.modalPadding * 2 : 0;
}, Media.Mobile(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    max-width: ", ";\n    max-height: ", ";\n    height: ", ";\n    top: 0;\n    border-radius: ", ";\n    width: 100vw;\n  "])), function (props) {
  return props.isFullScreenOnMobile ? 'none' : "calc(100vw - ".concat(DEFAULT_MODAL_PADDING, "px)");
}, function (props) {
  return props.isFullScreenOnMobile ? '100%' : getMaxHeightForSize(props);
}, function (props) {
  return props.isFullScreenOnMobile ? MOBILE_HEIGHT : 'auto';
}, function (props) {
  return props.isFullScreenOnMobile ? BorderRadius.None : getBorderRadiusForSize(props);
}));

var getDividerStyles = function getDividerStyles(side) {
  return function (_ref) {
    var theme = _ref.theme,
        useHairlineDividers = _ref.useHairlineDividers,
        showDivider = _ref.showDivider;

    if (useHairlineDividers) {
      var ruleName = side === 'top' ? 'border-top' : 'border-bottom';
      return css(["", ":", "px solid ", ";"], ruleName, theme.BorderWidth.Hairline, theme.Colors.BorderPrimary);
    }

    return showDivider ? css(["box-shadow:0px calc(", " * 1px) 15px rgba(0,0,0,0.2);"], side === 'top' ? -1 : 1) : css(["box-shadow:0px calc(", " * 1px) 0px rgba(0,0,0,0);"], side === 'top' ? -1 : 1);
  };
};

styled.div.withConfig({
  displayName: "styles__ModalContentHeader",
  componentId: "sc-1r4qbfh-1"
})(["flex-shrink:0;text-align:", ";"], function (props) {
  return props.headerAlignment;
});
var ModalStickyHeader = withTheming(styled.div.withConfig({
  displayName: "styles__ModalStickyHeader",
  componentId: "sc-1r4qbfh-2"
})(["position:absolute;width:100%;background:", ";padding:", "px;border-radius:", ";border-bottom:1px solid ", ";transition:all 0.2s ease-in-out;z-index:1;", ";", ";"], function (props) {
  return props.modalBackgroundColor;
}, function (props) {
  return props.modalPadding - Spacing.Small;
}, function (props) {
  return "".concat(props.modalBorderRadius, "px ").concat(props.modalBorderRadius, "px 0 0");
}, function (_ref2) {
  var theme = _ref2.theme;
  return theme.Colors.BorderPrimary;
}, getDividerStyles('bottom'), Media.Mobile(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    border-radius: ", ";\n  "])), function (props) {
  return props.isFullScreenOnMobile && BorderRadius.None;
})));
var ModalContentContainer = styled.div.withConfig({
  displayName: "styles__ModalContentContainer",
  componentId: "sc-1r4qbfh-3"
})(["flex-grow:1;overflow:auto;-webkit-overflow-scrolling:touch;z-index:0;"]);
var ModalContent = withTheming(styled.div.withConfig({
  displayName: "styles__ModalContent",
  componentId: "sc-1r4qbfh-4"
})(["padding:", ";", " ", ""], function (props) {
  return !props.hasPadding ? '0px' : props.useDynamicPadding ? "0px ".concat(props.theme.Spacing.NavBarPaddingHorizontalLarge, "px ").concat(props.theme.Spacing.NavBarPaddingVerticalLarge, "px ").concat(props.theme.Spacing.NavBarPaddingHorizontalLarge, "px") : "0px ".concat(DEFAULT_MODAL_PADDING, "px ").concat(DEFAULT_MODAL_PADDING, "px ").concat(DEFAULT_MODAL_PADDING, "px");
}, Media.Tablet(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    padding: ", ";\n  "])), function (props) {
  return props.hasPadding && props.useDynamicPadding ? "0px ".concat(props.theme.Spacing.NavBarPaddingHorizontalMedium, "px ").concat(props.theme.Spacing.NavBarPaddingVerticalMedium, "px ").concat(props.theme.Spacing.NavBarPaddingHorizontalMedium, "px") : undefined;
}), Media.Mobile(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    padding: ", ";\n  "])), function (props) {
  return props.hasPadding && props.useDynamicPadding ? "0px ".concat(props.theme.Spacing.NavBarPaddingHorizontalSmall, "px ").concat(props.theme.Spacing.NavBarPaddingVerticalSmall, "px ").concat(props.theme.Spacing.NavBarPaddingHorizontalSmall, "px") : undefined;
})));
var ModalFooter = withTheming(styled.div.withConfig({
  displayName: "styles__ModalFooter",
  componentId: "sc-1r4qbfh-5"
})(["background:", ";padding:", ";border-radius:", ";display:flex;justify-content:flex-end;flex-direction:'row';", ";flex-shrink:0;overflow:hidden;", ";", ";", ";", " ", ";"], function (props) {
  return props.modalBackgroundColor;
}, function (_ref3) {
  var theme = _ref3.theme;
  return theme.Modal.hasDynamicPadding ? "".concat(theme.Spacing.ButtonGroupPaddingVerticalLarge, "px ").concat(theme.Spacing.ButtonGroupPaddingHorizontalLarge, "px") : "".concat(DEFAULT_MODAL_PADDING, "px");
}, function (props) {
  return "0 0 ".concat(props.modalBorderRadius, "px ").concat(props.modalBorderRadius, "px");
}, function (props) {
  return props.shouldStackButtons && css(["flex-direction:column-reverse;& > button:nth-child(2){margin-bottom:", "px;}"], Spacing.XSmall);
}, function (props) {
  return !props.shouldStackButtons && css(["& > button:nth-child(2){margin-left:", "px;}"], props.inlineActionButtonSpacing);
}, getDividerStyles('top'), function (props) {
  return props.actionsFillContainer && css(["& > *{flex-grow:1;flex-shrink:1;flex-basis:50%;}"]);
}, Media.Tablet(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      padding: ", ";\n  "])), function (_ref4) {
  var theme = _ref4.theme;
  return theme.Modal.hasDynamicPadding ? "".concat(theme.Spacing.ButtonGroupPaddingVerticalMedium, "px ").concat(theme.Spacing.ButtonGroupPaddingHorizontalMedium, "px") : undefined;
}), Media.Mobile(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    padding: ", ";\n    ", "\n    padding-bottom: ", ";\n    flex-direction: column-reverse;\n    & > button:nth-child(2) {\n      margin: 0 0 ", "px 0;\n    }\n    border-radius: ", ";\n  "])), function (_ref5) {
  var theme = _ref5.theme;
  return theme.Modal.hasDynamicPadding ? "".concat(theme.Spacing.ButtonGroupPaddingVerticalSmall, "px ").concat(theme.Spacing.ButtonGroupPaddingHorizontalSmall, "px") : undefined;
}, ''
/**
 * To account for the placement of browser elements in iOS (Safari in
 * particular), we are setting an `env()` variable for the footer's bottom
 * padding on mobile based on the safe inset area. `env()` is not available
 * on IE11, and we only need to apply this padding for mobile iOS devices,
 * so we check if this is a fullscreen Modal being rendered on an iOS device
 * to prevent any styling issues if `env()` is not supported.
 */
, function (props) {
  return props.isIOSFixedFullscreen ? "calc(env(safe-area-inset-bottom, 0px) + ".concat(DEFAULT_MODAL_PADDING, "px)") : "".concat(DEFAULT_MODAL_PADDING, "px");
}, Spacing.XSmall, function (props) {
  return props.isFullScreenOnMobile && BorderRadius.None;
}))); // Note: `env()` is not available in IE, but FixedModalFooterContainer
// is only rendered for fullscreen mobile modals with fixed footers on iOS,
// and `env()` is supported in all other major browsers. In addition, there
// is a fallback of 0px for the bottom placement if the environment variable
// is not available

var FixedModalFooterContainer = styled.div.attrs({
  // Fix to prevent the focus lock from transferring focus back inside the modal content
  // when the user clicks on an action button
  tabIndex: -1
}).withConfig({
  displayName: "styles__FixedModalFooterContainer",
  componentId: "sc-1r4qbfh-6"
})(["position:fixed;left:0;right:0;margin:auto;bottom:", ";max-width:", ";width:calc(100vw - ", "px);", ";"], function (props) {
  return props.mobileBottomLocation > 0 ? "".concat(props.mobileBottomLocation, "px") : 'env(safe-area-inset-bottom, 0px)';
}, function (props) {
  return getMaxWidthForSize({
    size: props.modalSize
  });
}, function (props) {
  return props.modalPadding * 2;
}, Media.Mobile(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    ", "\n    bottom: ", "px;\n    width: 100vw;\n    max-width: ", ";\n  "])), ''
/*
 * Default to keeping the fixed footer on the bottom, unless the
 * mobile bottom location is greater than 0 (meaning there is a
 * smart banner pushing Modal content down)
 */
, function (props) {
  return props.mobileBottomLocation > 0 ? props.mobileBottomLocation : 0;
}, function (props) {
  return props.isFullScreenOnMobile ? 'none' : "calc(100vw - ".concat(DEFAULT_MODAL_PADDING, "px)");
}));
var InContentModalFooterContainer = styled.div.withConfig({
  displayName: "styles__InContentModalFooterContainer",
  componentId: "sc-1r4qbfh-7"
})(["z-index:1;transition:all 0.3s ease-in-out;"]);
var LayeredFooterContainer = renderOnLayer(Layers.PopoverModal, FixedModalFooterContainer, {
  AnimationType: AnimationTypes.None
});
var ModalFooterContainer = function ModalFooterContainer(_ref6) {
  var isFooterFixedPosition = _ref6.isFooterFixedPosition,
      props = _objectWithoutProperties(_ref6, ["isFooterFixedPosition"]);

  return isFooterFixedPosition && props.isFullScreenOnMobile && OS === 'iOS' ? /*#__PURE__*/React.createElement(LayeredFooterContainer, props) : /*#__PURE__*/React.createElement(InContentModalFooterContainer, props);
};
ModalFooterContainer.propTypes = {
  isFooterFixedPosition: PropTypes.bool,
  isFullScreenOnMobile: PropTypes.bool
};
ModalFooterContainer.defaultProps = {
  isFooterFixedPosition: false
};
var HeaderImageWrapper = styled.div.withConfig({
  displayName: "styles__HeaderImageWrapper",
  componentId: "sc-1r4qbfh-8"
})(["border-top-left-radius:", "px;border-top-right-radius:", "px;flex-shrink:0;overflow:hidden;text-align:center;", ""], function (props) {
  return props.modalBorderRadius;
}, function (props) {
  return props.modalBorderRadius;
}, function (props) {
  return props.isFullScreenOnMobile && css(["", ""], Media.Mobile(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n        border-top-left-radius: 0px;\n        border-top-right-radius: 0px;\n      "]))));
});
styled.div.withConfig({
  displayName: "styles__StickyTitle",
  componentId: "sc-1r4qbfh-9"
})(["flex-grow:1;overflow:hidden;& > *{white-space:nowrap;text-overflow:ellipsis;overflow:hidden;}"]);
var HeaderNavBarWrapper = withTheming(styled.div.withConfig({
  displayName: "styles__HeaderNavBarWrapper",
  componentId: "sc-1r4qbfh-10"
})(["padding:", ";padding-bottom:", ";", " ", ""], function (props) {
  return props.noPadding ? '0px' : props.useDynamicPadding ? "".concat(props.theme.Spacing.NavBarPaddingVerticalLarge, "px ").concat(props.theme.Spacing.NavBarPaddingHorizontalLarge, "px") : "".concat(DEFAULT_MODAL_PADDING, "px");
}, function (props) {
  return props.removeBottomPadding ? '0px' : undefined;
}, Media.Tablet(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    padding: ", ";\n    padding-bottom: ", ";\n  "])), function (props) {
  return !props.noPadding && props.useDynamicPadding ? "".concat(props.theme.Spacing.NavBarPaddingVerticalMedium, "px ").concat(props.theme.Spacing.NavBarPaddingHorizontalMedium, "px") : undefined;
}, function (props) {
  return props.removeBottomPadding ? '0px' : undefined;
}), Media.Mobile(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    padding: ", ";\n    padding-bottom: ", ";\n  "])), function (props) {
  return !props.noPadding && props.useDynamicPadding ? "".concat(props.theme.Spacing.NavBarPaddingVerticalSmall, "px ").concat(props.theme.Spacing.NavBarPaddingHorizontalSmall, "px") : undefined;
}, function (props) {
  return props.removeBottomPadding ? '0px' : undefined;
})));
var ModalCloseButtonContainer = withTheming(styled.div.withConfig({
  displayName: "styles__ModalCloseButtonContainer",
  componentId: "sc-1r4qbfh-11"
})(["width:100%;display:flex;flex-direction:", ";padding:", ";", ";", " ", ""], function (props) {
  return props.closeButtonSide === CloseButtonSides.Trailing ? 'row-reverse' : 'row';
}, function (props) {
  return props.useDynamicPadding ? "".concat(props.theme.Spacing.NavBarPaddingVerticalLarge, "px ").concat(props.theme.Spacing.NavBarPaddingHorizontalLarge - Spacing.XxSmall, "px") : "".concat(DEFAULT_MODAL_PADDING - Spacing.XxSmall, "px");
}, function (props) {
  return props.isFloating && css(["position:absolute;z-index:1;"]);
}, Media.Tablet(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    padding: ", ";\n  "])), function (props) {
  return props.useDynamicPadding ? "".concat(props.theme.Spacing.NavBarPaddingVerticalMedium, "px ").concat(props.theme.Spacing.NavBarPaddingHorizontalMedium - Spacing.XxSmall, "px") : undefined;
}), Media.Mobile(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    padding: ", ";\n  "])), function (props) {
  return props.useDynamicPadding ? "".concat(props.theme.Spacing.NavBarPaddingVerticalSmall, "px ").concat(props.theme.Spacing.NavBarPaddingHorizontalSmall - Spacing.XxSmall, "px") : undefined;
})));
var FillScreen = styled.div.withConfig({
  displayName: "styles__FillScreen",
  componentId: "sc-1r4qbfh-12"
})(["z-index:1;width:100vw;height:100vh;position:fixed;top:0;left:0;display:flex;justify-content:center;align-items:center;", ";pointer-events:none;& > *{pointer-events:auto;}"], Media.Mobile(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    height: ", ";\n  "])), MOBILE_HEIGHT));
var OverlayPositioner = styled.div.withConfig({
  displayName: "styles__OverlayPositioner",
  componentId: "sc-1r4qbfh-13"
})(["position:fixed;width:100%;height:100%;top:0;left:0;"]);

export { FillScreen, FixedModalFooterContainer, HeaderImageWrapper, HeaderNavBarWrapper, InContentModalFooterContainer, LayeredFooterContainer, ModalCloseButtonContainer, ModalContainer, ModalContent, ModalContentContainer, ModalFooter, ModalFooterContainer, ModalStickyHeader, OverlayPositioner };
